/**
 * Shared navigation data layer (Movement spec §1.1). ONE authoritative structure
 * every non-player agent reads from — world animals, night mobs, arena bots. No
 * agent raycasts the tilemap ad-hoc; they all read this grid.
 *
 * Built once per map generation, rebuilt only on terrain edits. A source classifies
 * each tile (blocked?, base cost, flags); this module derives the rest: clearance
 * (distance to the nearest blocker, for size-aware pathing) and regionId (connected
 * components, so an unreachable target is rejected in O(1) before A* floods the map).
 */

/** Tile flags, one bit each (spec §1.1). Fits a byte. */
export const enum NavFlag {
  WATER = 1,
  BRIDGE = 2,
  RAMP = 4,
  CHOKEPOINT = 8,
  COVER = 16,
  LIT = 32,
  HAZARD = 64,
}

/** Default terrain costs (spec §1.1 cost table). ∞ = never pathable. */
export const COST = {
  ground: 1.0,      // grass / basalt
  road: 0.6,        // agents visibly prefer roads — reads as "human"
  tallGrass: 1.6,   // tall grass / mud
  shroom: 2.0,       // slows, but hides — Tacticians weigh it negative
  bridge: 1.5,      // + CHOKEPOINT flag
  ramp: 1.3,
  shallowWater: 2.5,
  lava: Infinity,   // no exception anywhere in the cost function
} as const;

export interface NavGrid {
  w: number;
  h: number;
  walkable: Uint8Array;   // 1 = walkable
  cost: Float32Array;     // static base cost; Infinity where blocked
  clearance: Uint8Array;  // Chebyshev distance to nearest blocker (0 = blocker), capped 255
  flags: Uint8Array;      // NavFlag bitmask
  regionId: Uint16Array;  // connected-component id (0 = blocked / no region)
}

/** What a map hands the nav layer per tile. */
export interface TileSource {
  w: number;
  h: number;
  /** Blocked (∞-cost) tile — never walkable. */
  blocked(x: number, y: number): boolean;
  /** Static movement cost for a walkable tile (≥ some floor; roads < 1). */
  cost(x: number, y: number): number;
  /** NavFlag bitmask for the tile. */
  flags(x: number, y: number): number;
}

export const tileIndex = (x: number, y: number, w: number) => y * w + x;

/**
 * Diagonal step legality shared by regions AND pathfinding: a diagonal A→B is legal
 * only if both orthogonal neighbours shared by A and B are walkable — no cutting
 * through a blocker's corner (spec §2.1). Exported so the pathfinder reuses the
 * exact same rule the region graph was built with.
 */
export function diagonalOpen(walk: Uint8Array, w: number, h: number, x: number, y: number, dx: number, dy: number): boolean {
  if (dx === 0 || dy === 0) return true;
  const ax = x + dx, ay = y;
  const bx = x, by = y + dy;
  if (ax < 0 || ax >= w || by < 0 || by >= h) return false;
  return walk[tileIndex(ax, ay, w)] === 1 && walk[tileIndex(bx, by, w)] === 1;
}

/** Build the static nav grid from a tile source. */
export function buildNavGrid(src: TileSource): NavGrid {
  const { w, h } = src;
  const n = w * h;
  const walkable = new Uint8Array(n);
  const cost = new Float32Array(n);
  const flags = new Uint8Array(n);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const i = tileIndex(x, y, w);
    flags[i] = src.flags(x, y) & 0xff;
    if (src.blocked(x, y)) { walkable[i] = 0; cost[i] = Infinity; }
    else { walkable[i] = 1; cost[i] = src.cost(x, y); }
  }
  return {
    w, h, walkable, cost, flags,
    clearance: computeClearance(walkable, w, h),
    regionId: computeRegions(walkable, w, h),
  };
}

/**
 * Chebyshev distance-to-nearest-blocker via a two-pass chamfer transform. Blockers
 * (and the outside of the map) are 0; open interior grows outward. Capped at 255.
 */
export function computeClearance(walk: Uint8Array, w: number, h: number): Uint8Array {
  const d = new Uint8Array(w * h);
  const INF = 255;
  for (let i = 0; i < d.length; i++) d[i] = walk[i] ? INF : 0;
  // Forward pass (top-left → bottom-right).
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const i = tileIndex(x, y, w);
    if (d[i] === 0) continue;
    let m = INF;
    for (const [dx, dy] of [[-1, 0], [0, -1], [-1, -1], [1, -1]] as const) {
      const nx = x + dx, ny = y + dy;
      const nb = (nx < 0 || ny < 0 || nx >= w || ny >= h) ? 0 : d[tileIndex(nx, ny, w)];
      m = Math.min(m, nb + 1);
    }
    d[i] = Math.min(d[i], m);
  }
  // Backward pass (bottom-right → top-left).
  for (let y = h - 1; y >= 0; y--) for (let x = w - 1; x >= 0; x--) {
    const i = tileIndex(x, y, w);
    if (d[i] === 0) continue;
    let m = d[i];
    for (const [dx, dy] of [[1, 0], [0, 1], [1, 1], [-1, 1]] as const) {
      const nx = x + dx, ny = y + dy;
      const nb = (nx < 0 || ny < 0 || nx >= w || ny >= h) ? 0 : d[tileIndex(nx, ny, w)];
      m = Math.min(m, nb + 1);
    }
    d[i] = Math.min(m, INF);
  }
  return d;
}

/**
 * Connected-component region ids over walkable tiles, using the SAME 8-dir adjacency
 * (no corner-cutting) that the mover and pathfinder use — so two tiles share a region
 * iff an agent can actually walk between them. regionId(start) === regionId(goal) is
 * the O(1) unreachability check that stops 25 bots flood-filling across a lava river.
 */
export function computeRegions(walk: Uint8Array, w: number, h: number): Uint16Array {
  const region = new Uint16Array(w * h);   // 0 = blocked / unassigned
  let next = 1;
  const stack: number[] = [];
  const DIRS = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]] as const;
  for (let s = 0; s < region.length; s++) {
    if (!walk[s] || region[s]) continue;
    const id = next++;
    stack.length = 0; stack.push(s); region[s] = id;
    while (stack.length) {
      const i = stack.pop()!;
      const x = i % w, y = (i - x) / w;
      for (const [dx, dy] of DIRS) {
        const nx = x + dx, ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
        const ni = tileIndex(nx, ny, w);
        if (!walk[ni] || region[ni]) continue;
        if (!diagonalOpen(walk, w, h, x, y, dx, dy)) continue;   // no diagonal corner cut
        region[ni] = id; stack.push(ni);
      }
    }
  }
  return region;
}

/** O(1) reachability: are start and goal in the same walkable region? */
export function sameRegion(grid: NavGrid, sx: number, sy: number, gx: number, gy: number): boolean {
  if (sx < 0 || sy < 0 || sx >= grid.w || sy >= grid.h) return false;
  if (gx < 0 || gy < 0 || gx >= grid.w || gy >= grid.h) return false;
  const a = grid.regionId[tileIndex(sx, sy, grid.w)];
  const b = grid.regionId[tileIndex(gx, gy, grid.w)];
  return a !== 0 && a === b;
}
